/**
 * SDK custom parameters — data layer (feat #205).
 *
 * Thin typed wrapper over `apiClient` for the SDK 10 custom-param label
 * mapping + event debug endpoints:
 *
 *   GET  /api/v1/sdk-custom-params             — getMapping
 *   PUT  /api/v1/sdk-custom-params             — updateMapping
 *   POST /api/v1/sdk-custom-params/validate    — validateMapping (dry-run)
 *   GET  /api/v1/sdk-custom-params/events      — listEvents (debug)
 *   POST /api/v1/sdk-custom-params/events      — storeEvent
 *
 * Pure helpers (validators, formatters, query keys, label resolution) live
 * here too so they can be unit-tested without a DOM or TanStack context.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const PARAM_COUNT = 10;

export const PARAM_KEYS = [
	"param1",
	"param2",
	"param3",
	"param4",
	"param5",
	"param6",
	"param7",
	"param8",
	"param9",
	"param10",
] as const;

export type ParamKey = (typeof PARAM_KEYS)[number];

export const RESERVED_LABELS: readonly string[] = [
	"event_name",
	"tenant_id",
	"click_id",
	"created_at",
];

export const LABEL_RE = /^[a-z][a-z0-9_]{0,29}$/;

// ---------------------------------------------------------------------------
// Row shapes — mirror server response exactly.
// ---------------------------------------------------------------------------

export type ParamLabelView = Record<ParamKey, string | null> & {
	updated_at: number;
};

export interface SdkCustomEventView {
	id: string;
	event_name: string;
	param1: string | null;
	param2: string | null;
	param3: string | null;
	param4: string | null;
	param5: string | null;
	param6: string | null;
	param7: string | null;
	param8: string | null;
	param9: string | null;
	param10: string | null;
	created_at: number;
}

export interface LabeledEventView {
	id: string;
	event_name: string;
	created_at: number;
	raw: Record<ParamKey, string | null>;
	labeled: Record<string, string>;
}

export interface GetMappingResponse {
	mapping: ParamLabelView;
}

export interface UpdateMappingResponse {
	mapping: ParamLabelView;
}

export interface ValidationIssue {
	path: Array<string | number>;
	message: string;
}

export interface ValidateMappingResponse {
	valid: boolean;
	issues: ValidationIssue[];
}

export interface ListEventsResponse {
	events: SdkCustomEventView[];
	labeled: LabeledEventView[];
	mapping: ParamLabelView;
}

export interface StoreEventResponse {
	event: SdkCustomEventView;
}

// ---------------------------------------------------------------------------
// Validators — mirror server contract so the UI can short-circuit obvious
// errors before the network round-trip.
// ---------------------------------------------------------------------------

export type LabelValidationReason =
	| "empty"
	| "too_long"
	| "invalid_format"
	| "reserved_word";

/**
 * Check a single label string. Returns `null` when valid, otherwise one of
 * the reason codes above.
 *
 * Empty string (or whitespace-only) is explicitly valid here — it means
 * "clear this slot" in the PUT payload.
 */
export function validateLabel(raw: string): LabelValidationReason | null {
	if (typeof raw !== "string") return "empty";
	const trimmed = raw.trim();
	if (trimmed.length === 0) return null; // clear
	if (trimmed.length > 30) return "too_long";
	if (!LABEL_RE.test(trimmed)) return "invalid_format";
	if (RESERVED_LABELS.includes(trimmed)) return "reserved_word";
	return null;
}

export interface MappingValidationIssue {
	slot: ParamKey;
	reason: LabelValidationReason | "duplicate_label";
}

/**
 * Validate the whole mapping in one pass — per-slot format rules plus the
 * cross-slot uniqueness guarantee.
 */
export function validateMappingLocally(
	mapping: Partial<Record<ParamKey, string>>,
): MappingValidationIssue[] {
	const issues: MappingValidationIssue[] = [];
	const cleaned = new Map<ParamKey, string>();
	for (const slot of PARAM_KEYS) {
		const raw = mapping[slot];
		if (raw === undefined) continue;
		const reason = validateLabel(raw);
		if (reason) {
			issues.push({ slot, reason });
			continue;
		}
		const trimmed = raw.trim();
		if (trimmed.length > 0) cleaned.set(slot, trimmed);
	}
	const seen = new Map<string, ParamKey>();
	for (const [slot, label] of cleaned) {
		if (seen.has(label)) {
			issues.push({ slot, reason: "duplicate_label" });
		} else {
			seen.set(label, slot);
		}
	}
	return issues;
}

// ---------------------------------------------------------------------------
// Formatters / resolvers — pure, DOM-free.
// ---------------------------------------------------------------------------

/**
 * Given a slot key and the current mapping, return the display label
 * (mapping when present, raw paramN otherwise).
 */
export function resolveLabel(
	slot: ParamKey,
	mapping: ParamLabelView | null | undefined,
): string {
	const mapped = mapping?.[slot];
	if (typeof mapped === "string" && mapped.length > 0) return mapped;
	return slot;
}

/**
 * Return an ordered list of [slot, resolvedLabel] pairs — convenient for
 * rendering table headers.
 */
export function resolveAllLabels(
	mapping: ParamLabelView | null | undefined,
): Array<{ slot: ParamKey; label: string; mapped: boolean }> {
	return PARAM_KEYS.map((slot) => {
		const v = mapping?.[slot];
		const hasMapping = typeof v === "string" && v.length > 0;
		return {
			slot,
			label: hasMapping ? (v as string) : slot,
			mapped: hasMapping,
		};
	});
}

export function formatUnixSec(unixSec: number | null | undefined): string {
	if (unixSec == null) return "Never";
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export function describeIssue(issue: ValidationIssue): string {
	const path = issue.path.join(".") || "(root)";
	const msg = issue.message;
	const readable: Record<string, string> = {
		required: "must not be blank",
		too_long: "is too long (max 30 chars)",
		invalid_format: "must be snake_case starting with a letter",
		reserved_word: "is a reserved word",
		duplicate_label: "is used in another slot",
		unknown_key: "is not a known param slot",
		expected_string: "must be a string",
		expected_object: "must be an object",
	};
	return `${path}: ${readable[msg] ?? msg}`;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const sdkCustomParamsQueryKeys = {
	all: ["sdk-custom-params"] as const,
	mapping: () => [...sdkCustomParamsQueryKeys.all, "mapping"] as const,
	events: (limit?: number) =>
		limit !== undefined
			? ([...sdkCustomParamsQueryKeys.all, "events", limit] as const)
			: ([...sdkCustomParamsQueryKeys.all, "events"] as const),
};

// ---------------------------------------------------------------------------
// Remote bindings — thin `apiClient` shims.
// ---------------------------------------------------------------------------

const BASE = "/sdk-custom-params";

export async function getMapping(): Promise<ParamLabelView> {
	const res = await apiClient.get<GetMappingResponse>(BASE);
	return res.mapping;
}

export async function updateMapping(
	patch: Partial<Record<ParamKey, string | null>>,
): Promise<ParamLabelView> {
	const res = await apiClient.put<UpdateMappingResponse>(BASE, patch);
	return res.mapping;
}

export async function validateMapping(
	patch: Partial<Record<ParamKey, string | null>>,
): Promise<ValidateMappingResponse> {
	return apiClient.post<ValidateMappingResponse>(`${BASE}/validate`, patch);
}

export interface StoreEventArgs {
	event_name: string;
	params?: Partial<Record<ParamKey, string | null>>;
}

export async function storeEvent(
	args: StoreEventArgs,
): Promise<SdkCustomEventView> {
	const res = await apiClient.post<StoreEventResponse>(
		`${BASE}/events`,
		args,
	);
	return res.event;
}

export async function listEvents(limit = 50): Promise<ListEventsResponse> {
	return apiClient.get<ListEventsResponse>(`${BASE}/events`, {
		query: { limit },
	});
}
